import React from 'react'

import { Autocomplete } from '@masfront/common-b2c'
import { Field } from 'formik'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import { Skeleton } from '@material-ui/lab'

import useAddressInput from './useAddressInput'

import { Address, AddressType } from '../models/address.model'

import { matchHighlightString, parseHighlightString } from '~/core/helpers/helpers'

interface AddressInputProps {
  id: string
  addressType: AddressType
}

export default function AddressInput({ id, addressType = AddressType.SIM }: AddressInputProps) {
  const {
    options,
    isLoading,
    value,
    inputValue,
    classes,
    setInputValue,
    setOptions,
    setAddress,
    resetAddress,
  } = useAddressInput(id, addressType)

  return (
    <Field
      component={Autocomplete}
      name={`address-input-${id}`}
      getOptionLabel={(option: Address | string) =>
        typeof option === 'string' ? option : option?.summary || ''
      }
      label="Introducir dirección"
      filterOptions={(x: Address) => x}
      options={options}
      autoComplete={false}
      includeInputInList
      freeSolo
      value={value}
      filterSelectedOptions
      loading={isLoading}
      loadingText={isLoading && <Skeleton variant="text" width="100%" height={24} />}
      onChange={(event: React.ChangeEvent<HTMLInputElement>, newValue: Address | string) => {
        const address =
          typeof newValue === 'string'
            ? ({ streetName: newValue, summary: newValue } as Address)
            : newValue
        setOptions(address ? [address, ...options] : options)
        if (address) {
          setAddress({
            ...address,
            addressInput: `${address?.streetType} ${address?.streetName}` || '',
          })
        } else {
          resetAddress()
        }
      }}
      onInputChange={(event: React.ChangeEvent<HTMLInputElement>, newInputValue: string) => {
        setInputValue(newInputValue)
        /* eslint-disable-next-line */
        const { addressInput, ...rest } = value
        if (Object.values(rest).some((i) => !!i)) {
          resetAddress()
        }
      }}
      renderOption={(option: Address) => {
        const opt = option?.summary || ''
        const matches = matchHighlightString(opt, inputValue)
        const parts = parseHighlightString(opt, matches)
        return (
          <Grid container alignItems="center" data-hook="address-option">
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {option?.gescal ? (
                parts.map((part, index) => {
                  return (
                    <span
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${option.gescal}-${index}`}
                      className={part.highlight ? classes.bold : classes.normal}
                    >
                      {part?.text}
                    </span>
                  )
                })
              ) : (
                <span className={classes.normal}>Utilizar la dirección introducida </span>
              )}
              <Typography variant="body2" color="textSecondary">
                {option?.summary}
              </Typography>
            </Grid>
          </Grid>
        )
      }}
    />
  )
}
